import React, { useState, useEffect } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import SongCard from './components/SongCard';
import Timeline from './components/Timeline';
import GameActions from './components/GameActions';
import MessageArea from './components/MessageArea';
import Instructions from './components/Instructions';
import CollectionSelector from './components/CollectionSelector';
import useGame from './hooks/useGame'; // Import the new hook
import styles from './App.module.css';

function App() {
  const {
    currentSong,
    round,
    gameState,
    messages,
    songRevealed,
    yearRevealed,
    players,
    currentPlayer,
    selectedCollectionFile,
    handleSelectCollection,
    startNewGame,
    nextRound,
    revealSong,
    revealYear,
    handleSongPlacement,
    handlePlayerNameChange,
  } = useGame();

  const [activeId, setActiveId] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  // Configure sensors to require minimum drag distance (prevents accidental clicks)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before starting drag
      },
    })
  );

  // Close hamburger menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest(`.${styles.hamburger_menu}`)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    console.log('Drag ended:', event.over?.id);
    setActiveId(null);

    if (!event.over || !currentSong) {
      console.log('No drop target or current song - drag cancelled');
      return;
    }

    // Only handle drops on valid drop zones (format: drop-zone-{playerId}-{position})
    if (!event.over.id.startsWith('drop-zone-')) {
      console.log('Invalid drop target:', event.over.id);
      return;
    }

    console.log('Valid drop target:', event.over.id);
    const dropZoneId = event.over.id.replace('drop-zone-', '');
    const [playerId, position] = dropZoneId.split('-');

    // Validate parsing results
    const dropPosition = parseInt(position);
    const playerIdInt = parseInt(playerId);

    if (isNaN(dropPosition) || isNaN(playerIdInt) || dropPosition < 0 || playerIdInt < 1) {
      console.log('Invalid drop zone format - cancelling placement');
      return;
    }

    console.log('Drop position:', dropPosition, 'Player ID:', playerIdInt);
    handleSongPlacement(dropPosition, playerIdInt);
  };

  // Modal overlay logic
  const showModal = gameState === 'neutral' || gameState === 'selecting' || gameState === 'finished';

  let modalContent = null;
  if (gameState === 'neutral') {
    modalContent = (
      <div className={styles.modal_content}>
        <div className={styles.modal_title}>Welcome to Trickster</div>
        <button className={`btn btn-primary ${styles.new_game_btn}`} onClick={startNewGame}>
          Start New Game
        </button>
      </div>
    );
  } else if (gameState === 'selecting') {
    modalContent = (
      <div className={styles.modal_content}>
        <CollectionSelector
          onSelectCollection={handleSelectCollection}
          selectedCollectionFile={selectedCollectionFile}
        />
      </div>
    );
  } else if (gameState === 'finished') {
    modalContent = (
      <div className={styles.modal_content}>
        <div className={styles.finished_message}>🎉 Game Complete!</div>
        <button className={`btn btn-primary ${styles.new_game_btn}`} onClick={startNewGame}>
          New Game
        </button>
      </div>
    );
  }

  return (
    <div className={styles.board_game_container}>
      <Instructions />

      {/* Modal overlay for neutral/selecting/finished states */}
      {showModal && (
        <div className={styles.modal_overlay}>
          {modalContent}
        </div>
      )}

      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        {/* Player 1 Section (Top - Flipped) */}
        <div
          className={`${styles.player_section} ${styles.player_1_section} ${currentPlayer === 1 ? styles.active_player : styles.inactive_player}`}
        >
          <div className={styles.card_area_for_player}>
            <div className={styles.song_card_container}>
              {gameState === 'playing' && currentSong && currentPlayer === 1 ? (
                <SongCard
                  song={currentSong}
                  revealed={songRevealed}
                  yearRevealed={yearRevealed}
                  draggable={true}
                  onReveal={revealSong}
                  onRevealYear={revealYear}
                />
              ) : (
                <div className={styles.song_card_placeholder} />
              )}
            </div>
          </div>
          <Timeline
            timeline={players[0]?.timeline || []}
            isDragging={!!activeId}
            previewSong={currentSong}
            playerId={1}
            isPlayerActive={currentPlayer === 1}
            playerName={players[0]?.name || 'Player 1'}
          />
          <div className={`${styles.player_info_sidebar} ${styles.player_1_sidebar}`}>
            <div className={styles.player_stats_compact}>
              <div
                className={styles.player_name_editable}
                onDoubleClick={() =>
                  handlePlayerNameChange(
                    1,
                    prompt('Enter new name:', players[0]?.name) || players[0]?.name
                  )
                }
              >
                {players[0]?.name || 'Player 1'}
              </div>
              <div className={styles.player_score}>Score: {players[0]?.score || 0}</div>
              <div className={styles.player_streak}>Streak: {players[0]?.streak || 0}</div>
            </div>
          </div>
        </div>

        {/* Corner Title */}
        <div className={styles.corner_title}>
          <div className={styles.game_title}>Trickster</div>
          <div className={styles.round_info}>Round {round}</div>
        </div>

        {/* Hamburger Menu */}
        <div className={styles.hamburger_menu}>
          <button className={styles.hamburger_btn} onClick={() => setShowMenu(!showMenu)}>
            ☰
          </button>
          {showMenu && (
            <div className={styles.hamburger_dropdown}>
              <GameActions
                onReveal={() => {
                  revealSong();
                  setShowMenu(false);
                }}
                onRevealYear={() => {
                  revealYear();
                  setShowMenu(false);
                }}
                onNewGame={() => {
                  startNewGame();
                  setShowMenu(false);
                }}
                onNextSong={() => {
                  nextRound();
                  setShowMenu(false);
                }}
                gameState={gameState}
                currentSong={currentSong}
                songRevealed={songRevealed}
                yearRevealed={yearRevealed}
              />
            </div>
          )}
        </div>

        {/* Player 2 Section (Bottom - Normal) */}
        <div className={`${styles.player_section} ${styles.player_2_section} ${currentPlayer === 2 ? styles.active_player : styles.inactive_player}`}>
          <div className={styles.card_area_for_player}>
            <div className={styles.song_card_container}>
              {gameState === 'playing' && currentSong && currentPlayer === 2 ? (
                <SongCard
                  song={currentSong}
                  revealed={songRevealed}
                  yearRevealed={yearRevealed}
                  draggable={true}
                  onReveal={revealSong}
                  onRevealYear={revealYear}
                />
              ) : (
                <div className={styles.song_card_placeholder} />
              )}
            </div>
          </div>
          <Timeline
            timeline={players[1]?.timeline || []}
            isDragging={!!activeId}
            previewSong={currentSong}
            playerId={2}
            isPlayerActive={currentPlayer === 2}
            playerName={players[1]?.name || 'Player 2'}
          />
          <div className={`${styles.player_info_sidebar} ${styles.player_2_sidebar}`}>
            <div className={styles.player_stats_compact}>
              <div
                className={styles.player_name_editable}
                onDoubleClick={() =>
                  handlePlayerNameChange(
                    2,
                    prompt('Enter new name:', players[1]?.name) || players[1]?.name
                  )
                }
              >
                {players[1]?.name || 'Player 2'}
              </div>
              <div className={styles.player_score}>Score: {players[1]?.score || 0}</div>
              <div className={styles.player_streak}>Streak: {players[1]?.streak || 0}</div>
            </div>
          </div>
        </div>

        <DragOverlay>
          {activeId && currentSong && activeId === `song-${currentSong.id}` ? (
            <SongCard
              song={currentSong}
              revealed={songRevealed}
              yearRevealed={yearRevealed}
              draggable={false}
              overlay={true}
              onReveal={revealSong}
              onRevealYear={revealYear}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <MessageArea messages={messages.filter((m) => m.playerId !== 1)} playerPosition="top-right" />
      <MessageArea
        messages={messages.filter((m) => m.playerId === 1)}
        playerPosition="bottom-left"
      />
    </div>
  );
}

export default App;